import type { BookedFlightItineraryWithDepartureTime } from "@b2bportal/air-booking-api";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import styles from "./BookingDetailsSection.module.scss";
import { useBookingDetailsSection } from "./useBookingDetailsSection";

interface BookingDetailsSectionProps {
  itinerary: BookedFlightItineraryWithDepartureTime;
  className?: string;
}

export const BookingDetailsSection = ({
  itinerary,
  className,
}: BookingDetailsSectionProps) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "BookingDetailsSection");

  const {
    confirmationCode,
    airlineReferences,
    travellers,
    contactPhone,
    contactEmail,
    frequentFlyerNumbers,
    hasFrequentFlyer,
  } = useBookingDetailsSection(itinerary);

  return (
    <div className={clsx(block, className)}>
      <h3 className={cn("title")}>{t("flightTrips.bookingDetails")}</h3>

      {/* Confirmation Code */}
      <div className={cn("section")}>
        <div className={cn("field")}>
          <div className={cn("label")}>
            {t("tripSearch.confirmationCode")}
            <Icon iconName={IconNameEnum.info} className={cn("label-icon")} />
          </div>
          <p className={cn("value")}>{confirmationCode}</p>
        </div>
      </div>

      {/* Airline Reference(s) */}
      {airlineReferences.length > 0 && (
        <div className={cn("section")}>
          <div className={cn("field")}>
            <div className={cn("label")}>
              {airlineReferences.length === 1
                ? t("flightTrips.airlineReference")
                : t("flightTrips.airlineReferences")}
            </div>
            {airlineReferences.map((ref) => (
              <p
                key={`${ref.airlineCode}-${ref.locator}`}
                className={cn("value")}
              >
                {airlineReferences.length > 1 && `${ref.airlineName}: `}
                {ref.locator}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Travellers List */}
      {travellers.length > 0 && (
        <div className={cn("section")}>
          <div className={cn("field")}>
            <div className={cn("label")}>{t("flightTrips.travellers")}</div>
            <ul className={cn("travellers-list")}>
              {travellers.map((traveller) => (
                <li key={traveller.nameNumber}>
                  <p className={cn("value")}>
                    {traveller.givenName} {traveller.surname}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Contact Information */}
      {(contactPhone || contactEmail) && (
        <div className={cn("section")}>
          <div className={cn("field")}>
            <div className={cn("label")}>{t("contactInformation")}</div>
            {contactPhone && <p className={cn("value")}>{contactPhone}</p>}
            {contactEmail && (
              <a
                className={cn("value", ["link"])}
                href={`mailto:${contactEmail}`}
                target="_blank"
                rel="noreferrer"
              >
                {contactEmail}
              </a>
            )}
          </div>
        </div>
      )}

      {/* Frequent Flyer Numbers */}
      {hasFrequentFlyer && (
        <div className={cn("section")}>
          <div className={cn("field")}>
            <div className={cn("label")}>
              {t("flightTrips.frequentFlyerNumber")}
            </div>
            {Object.entries(frequentFlyerNumbers).map(([airline, number]) => (
              <p key={airline} className={cn("value")}>
                {airline}: {number}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
